import React from "react";
import PropTypes from "prop-types";

import _str from "shared/util/_str";

import ScaleFadeTransition from "shared/components/ScaleFadeTransition";
import ActionPrompt from "apps/messenger/components/MainWindow/MessagePane/ActionPrompt";
import ActionPromptButton from "./ActionPromptButton";
import LoadingIndicator from "apps/messenger/components/MainWindow/MessagePane/LoadingIndicator";
import Message from "apps/messenger/components/MainWindow/MessagePane/Message";

import {
	scrollToLastMessage,
	findMessagesToShowTimestampsFor,
	findMessageToShowReadIndicatorFor,
} from "apps/messenger/util/helpers";

import {
	WINDOW_STATE,
} from "apps/messenger/util/consts";

import "apps/messenger/components/MainWindow/MessagePane/index.scss";

class MessengerMessagePane extends React.PureComponent {
	static propTypes = {
		state: PropTypes.oneOf(Object.values(WINDOW_STATE)).isRequired,
		total: PropTypes.number.isRequired,
		loading: PropTypes.bool.isRequired,
		showUserBlock: PropTypes.bool.isRequired,
		onUnblock: PropTypes.func.isRequired,
		moveToSMSLoading: PropTypes.bool.isRequired,
		showMoveToSMS: PropTypes.bool.isRequired,
		onMoveToSMS: PropTypes.func.isRequired,
		showContentWarning: PropTypes.bool.isRequired,
		onDismissContentWarning: PropTypes.func.isRequired,
		onReport: PropTypes.func.isRequired,
		messages: PropTypes.arrayOf(PropTypes.shape({
			timestamp: PropTypes.number.isRequired,
		})).isRequired,
		userid: PropTypes.string.isRequired,
		loadMessages: PropTypes.func.isRequired,
	};

	constructor(props) {
		super(props);

		// Hydrate these caches for the first time here.
		this.timestampCache = findMessagesToShowTimestampsFor(props.messages);
		this.lastReadMessageCache = findMessageToShowReadIndicatorFor(props.messages);
		this.scrollArea = React.createRef();
	}

	componentDidMount() {
		this._handleScrollLogic(undefined, this.props);

		// If there's no messages loaded, try to load more.
		if (!this.props.messages.length) {
			this._loadMoreMessages();
		}
	}

	componentDidUpdate(prevProps, prevState, capturedScrollPosition) {
		// Makes sure scroll position is maintained as needed.
		this._handleScrollLogic(prevProps, this.props, capturedScrollPosition);

		// Update timestamp and last read message caches.
		this._updateCachedData(prevProps, this.props);
	}

	getSnapshotBeforeUpdate(prevProps) {
		const { messages: oldMessages } = prevProps;
		const { messages } = this.props;
		const scrollArea = this.scrollArea.current;

		// If the list changed, capture the current scroll position just in case.
		if (oldMessages !== messages) {
			return scrollArea.scrollHeight - scrollArea.scrollTop;
		}

		return null;
	}

	_handleScrollLogic = (prevProps = {}, nextProps = {}, capturedScrollPosition) => {
		// List of messages.
		const {
			messages,
			state,
			userid
		} = nextProps;
		const scrollArea = this.scrollArea.current;

		// We can't do anything in these conditions.
		if (!scrollArea || !messages.length || state === WINDOW_STATE.Minimized) {
			return;
		}

		const {
			messages: oldMessages = [],
			state: prevState,
			userid: prevUserid,
		} = prevProps;

		const userChanged = userid !== prevUserid;
		const windowWasMaximized = prevState === WINDOW_STATE.Minimized && state === WINDOW_STATE.Open;
		const messagesChanged = oldMessages !== messages;

		// We don't need to do anything in these conditions.
		if (!messagesChanged && !windowWasMaximized && !userChanged) {
			return;
		}

		const firstMessageLoad = !oldMessages.length && messages.length;
		const newestMessageChanged = oldMessages.length && messages.length && (
			oldMessages[oldMessages.length - 1].timestamp !== messages[messages.length - 1].timestamp
		);
		const oldestMessageChanged = oldMessages.length && messages.length && (
			oldMessages[0].timestamp !== messages[0].timestamp
		);

		// This is our first load of messages,
		// or we just unminimized the window,
		// or the user we're looking at changed,
		// or a new message arrived.
		// Scroll to the bottom.
		if (firstMessageLoad || windowWasMaximized || userChanged || newestMessageChanged) {
			scrollToLastMessage(scrollArea, {
				behavior: newestMessageChanged ? "smooth" : "instant",
				block: "start",
			});

			return;
		}

		// The new messages loaded are at the start
		// of the list, AKA we should restore the
		// scroll position pre-load.
		else if (oldestMessageChanged) {
			scrollArea.scrollTop = scrollArea.scrollHeight - capturedScrollPosition;

			return;
		}
	};

	_handleScroll = () => {
		// If we're near the top, load more messages.
		const scrollArea = this.scrollArea.current;
		const isNearTop = scrollArea.scrollTop < 100;

		if (isNearTop) {
			this._loadMoreMessages();
		}
	};

	_loadMoreMessages = () => {
		// Make sure we're not loading already,
		// and that there are more messages to load.
		const isLoading = this.props.loading;
		const hasMoreMessagesToLoad = this.props.total > this.props.messages.length;

		if (hasMoreMessagesToLoad && !isLoading) {
			this.props.loadMessages(this.props.userid);
		}
	};

	_updateCachedData = (prevProps, nextProps) => {
		if (!prevProps || !nextProps) {
			return;
		}

		// If the user changed or the messages changed, recalculate.
		// This can probably be optimized...
		const userChanged = prevProps.userid !== nextProps.userid;
		const messagesChanged = prevProps.messages !== nextProps.messages;

		if (userChanged || messagesChanged) {
			this.timestampCache = findMessagesToShowTimestampsFor(nextProps.messages);
			this.lastReadMessageCache = findMessageToShowReadIndicatorFor(nextProps.messages);

			this.forceUpdate();
		}
	};

	_shouldShowTimestampForMessage({ id, tmpId }) {
		if (this.timestampCache) {
			return this.timestampCache.indexOf(tmpId || id) >= 0;
		}

		return false;
	}

	_shouldShowReadIndicatorForMessage({ id }) {
		return id === this.lastReadMessageCache;
	}

	render() {
		const {
			messages,
			loading,

			showUserBlock,
			onUnblock,

			showContentWarning,
			onDismissContentWarning,
			onReport,

			moveToSMSLoading,
			showMoveToSMS,
			onMoveToSMS,

			userid,
		} = this.props;

		return (
			<div
				ref={this.scrollArea}
				onScroll={this._handleScroll}
				className="messenger-message-pane">
				{loading && <LoadingIndicator />}
				<ScaleFadeTransition speed={ScaleFadeTransition.speeds.Half}>
					{messages.map((message) => (
						<Message
							key={message.tmpId || message.id}
							{...message}
							conversationUserId={userid}
							showTimestamp={this._shouldShowTimestampForMessage(message)}
							showReadIndicator={this._shouldShowReadIndicatorForMessage(message)}
						/>
					))}
				</ScaleFadeTransition>
				{showUserBlock && (
					<ActionPrompt text={_str("You've blocked them.")}>
						<ActionPromptButton
							buttonType={ActionPromptButton.buttonType.primary}
							label={_str("Unblock")}
							onClick={() => onUnblock(userid)}
						/>
					</ActionPrompt>
				)}
				{showContentWarning && (
					<ActionPrompt text={_str("We noticed potentially inappropriate language. Do you want to report this message?")}>
						<ActionPromptButton
							buttonType={ActionPromptButton.buttonType.secondary}
							label={_str("No, this is fine")}
							onClick={() => onDismissContentWarning(userid)}
						/>
						<ActionPromptButton
							buttonType={ActionPromptButton.buttonType.primary}
							label={_str("Report It")}
							onClick={() => onReport(userid)}
						/>
					</ActionPrompt>
				)}
				{showMoveToSMS && (
					<ActionPrompt key="test">
						<ActionPromptButton
							buttonType={ActionPromptButton.buttonType.default}
							label={_str(moveToSMSLoading ? "Please wait" : "Move to SMS")}
							onClick={() => onMoveToSMS(userid)}
						/>
					</ActionPrompt>
				)}
			</div>
		);
	}
}

export default MessengerMessagePane;



// WEBPACK FOOTER //
// ./src/apps/messenger/components/MainWindow/MessagePane/index.jsx